/**
 * ApproverPicker — choose the humans an escalation tier contacts.
 *
 * WHAT THIS REPLACES, AND WHY IT MATTERS
 * --------------------------------------
 * A free-text box. Whatever an operator typed became the tier's `targetValue`, and
 * EscalationService.resolveTargetFromTier requires a USER target to be a user id — anything else
 * is caught and routed to resolveOrgAdminFallback. So every approver ever typed into that box
 * silently paged the org admin instead of the person named on screen. The live policy on this
 * platform had a single approver reading "IGA Synch Required", which was the most honest thing the
 * old UI could have shown.
 *
 * Selections here are user ids, which is what resolution actually needs.
 *
 * THE MANAGER HOP
 * ---------------
 * Each chosen person carries a mode: contact THEM, or contact THEIR MANAGER (targetType
 * MANAGER_OF, resolved from app_users.manager_id at page time, so a reorg re-routes without anyone
 * re-saving the policy). The manager's name is shown at selection time — an operator choosing
 * "their manager" should see who that is before saving, not discover it during an incident.
 *
 * Where a person has no manager on file the option is disabled with the reason, rather than
 * offered and silently falling back to the org admin. A local user who was never in the IGA is
 * perfectly selectable as themselves; they simply have no manager to escalate to.
 *
 * PAGING — WHY THE SERVER'S PAGE NUMBERS ARE HONOURED
 * ---------------------------------------------------
 * GET /api/v1/team/approvers has always been paged: it returns page, size, totalElements and
 * totalPages. This component asked for page 0 and threw every one of those numbers away, then
 * printed "Showing 20 of 412. Narrow the search to see the rest." — which is not a control, it is
 * an apology. On a synced enterprise directory the other 392 people were unreachable through this
 * UI unless an operator guessed a substring of a name they were trying to look up in the first
 * place. Paging is now driven off the server's own metadata, through the same footer EntityPicker
 * uses.
 *
 * A page turn is a fetch, not a slice. The directory is deliberately never held in memory here —
 * that is the whole reason the endpoint is paged.
 *
 * WHY THE PANEL MEASURES ITSELF
 * -----------------------------
 * This picker renders inside Slideout, whose body is `flex-1 overflow-y-auto`. An absolutely
 * positioned panel inside a scroll container is CLIPPED by that container, so a fixed max-height
 * produced a list whose last row was sliced in half and whose paging footer was below the cut,
 * unreachable and invisible — it looked like a dropdown with neither scroll nor paging, because
 * from the operator's side that is exactly what it was.
 *
 * So the panel measures the space its scroll parent actually leaves, opens upward when there is
 * more room above, and caps its own height to what will fit. The list is then the only scrolling
 * region inside it, which keeps the search box and the paging footer pinned and always reachable.
 * Re-measured on scroll (captured, so the slideout's own scrolling counts) and on resize.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Search, X, User, ChevronUp, AlertTriangle } from 'lucide-react';
import * as api from '../../services/api';
import { Pill } from './Pill';
import { PickerPagingFooter } from './PickerPagingFooter';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 250;
/** Below this the panel is not worth opening on that side; we would rather flip. */
const MIN_PANEL = 208;
/** Above this a dropdown stops reading as a dropdown and starts reading as a page. */
const MAX_PANEL = 420;
const GAP = 8;

/**
 * The box the panel must fit inside: the nearest scrolling ancestor, or the viewport.
 *
 * `overflow: hidden` counts. It clips absolutely positioned descendants exactly as `auto` does,
 * and a panel sized against the viewport inside one is invisible for the same reason.
 */
function clipBounds(el) {
  let n = el?.parentElement;
  while (n && n !== document.body && n !== document.documentElement) {
    const cs = window.getComputedStyle(n);
    if (/(auto|scroll|overlay|hidden)/.test(`${cs.overflowY} ${cs.overflowX} ${cs.overflow}`)) {
      const r = n.getBoundingClientRect();
      return { top: Math.max(0, r.top), bottom: Math.min(window.innerHeight, r.bottom) };
    }
    n = n.parentElement;
  }
  return { top: 0, bottom: window.innerHeight };
}

export function ApproverPicker({ value = [], onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({});
  const [filters, setFilters] = useState({ department: '', title: '', userType: '' });
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [panel, setPanel] = useState({ dir: 'down', maxHeight: MAX_PANEL });
  const rootRef = useRef(null);
  /* The panel is positioned against the TRIGGER, not against the whole control. Anchoring it to
     the outer element put it 81px above its own button with the chosen-approver list in between,
     floating over the unrelated fields above — measured in the browser, not guessed. */
  const anchorRef = useRef(null);
  /* Responses can land out of order — a slow page 1 arriving after a fast page 2 would silently
     show the wrong page under the right page number. Only the newest request may write state. */
  const seqRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.searchApprovers({
        q: debounced, ...filters, page, size: PAGE_SIZE,
      });
      if (seq !== seqRef.current) return;
      const body = res?.data ?? res;
      setRows(Array.isArray(body?.content) ? body.content : []);
      // Facets describe the caller's whole scope, not this page, so the server sends them once
      // with page 0. Keeping the ones we already have means turning a page never empties the
      // filter row — which would read as "your directory lost its departments".
      if (body?.facets && Object.keys(body.facets).length > 0) setFacets(body.facets);
      setTotal(Number(body?.totalElements || 0));
      setPageCount(Number(body?.totalPages || 0));
      setCursor(0);
    } catch (e) {
      if (seq !== seqRef.current) return;
      // A failed search must not look like an empty directory — that reads as "nobody to pick"
      // and an operator would go and create a user that already exists.
      setRows([]);
      setTotal(0);
      setPageCount(0);
      setError('We could not search your directory just now. Try again in a moment.');
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [debounced, filters, page]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const b = clipBounds(el);
    const below = b.bottom - r.bottom - GAP;
    const above = r.top - b.top - GAP;
    const dir = (below >= MIN_PANEL || below >= above) ? 'down' : 'up';
    const space = dir === 'down' ? below : above;
    setPanel({ dir, maxHeight: Math.max(MIN_PANEL, Math.min(MAX_PANEL, Math.floor(space))) });
  }, []);

  // useLayoutEffect, not useEffect: the panel renders with last open's height, and correcting it
  // after paint is a visible jump — worse, a first open that measures too tall paints clipped for
  // one frame, which is the exact symptom being fixed here.
  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    // Capture phase: the slideout body scrolls, not the window, and a bubbling listener on window
    // never sees it.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  /**
   * Escape closes THIS dropdown and goes no further.
   *
   * It used to be handled on the panel, which meant it only worked while focus was inside the
   * panel — and choosing a person DISABLES the row that was just clicked, so focus fell back to
   * document.body and the key never reached the handler. Escape then hit the Slideout's own
   * listener and closed the entire editor, discarding the selection the operator had just made.
   * Observed in the browser: one click, one Escape, and the unsaved approver was gone.
   *
   * Document-level and CAPTURE phase, so it runs before any other listener regardless of where
   * focus happens to be, and stopPropagation keeps it from reaching the Slideout at all. Only
   * while open — when the dropdown is shut, Escape belongs to the Slideout again.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener('keydown', onEsc, true);
    return () => document.removeEventListener('keydown', onEsc, true);
  }, [open]);

  // `value` is hardened to an array before anything else reads it.
  //
  // THE CRASH THIS FIXES
  // --------------------
  // "Add tier" seeded a new tier with `approvers: ''` — the scalar shape left over from the
  // free-text box this component replaced. This memo then ran `.map` on a string. A throw inside
  // useMemo happens during render, so React unwound to the nearest error boundary and the WHOLE
  // page went to "TypeError: n.map is not a function" — not just this control. The seed is fixed
  // at the call site; the guard stays here so no caller, now or later, can take a page down by
  // handing this component a legacy scalar.
  const chosen = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const chosenIds = useMemo(() => new Set(chosen.map((v) => v.id)), [chosen]);

  const add = (row) => {
    if (!row || chosenIds.has(row.id)) return;
    onChange?.([...chosen, {
      id: row.id,
      name: row.name,
      email: row.email,
      mode: 'USER',
      hasManager: !!row.hasManager,
      managerName: row.managerName || null,
    }]);
    // Clearing the search returns to page 0 of the unfiltered directory, which is where someone
    // adding a second person expects to start.
    setQuery('');
    setPage(0);
  };

  const remove = (id) => onChange?.(chosen.filter((v) => v.id !== id));

  const setMode = (id, mode) =>
    onChange?.(chosen.map((v) => (v.id === id ? { ...v, mode } : v)));

  /* A new search is a new result set, so it starts at its own first page rather than at page 4 of
     the previous one — where it would show an empty list and a footer insisting there are 300
     matches. Set alongside the query rather than in an effect on it: an effect would let one
     render fire a fetch for the old page before the reset landed. */
  const onQuery = (v) => { setQuery(v); setPage(0); };
  const onFilter = (key, v) => { setFilters((s) => ({ ...s, [key]: v })); setPage(0); };

  const onKeyDown = (e) => {
    /* Escape is handled at document level while open — see the effect above. */
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((i) => Math.min(i + 1, rows.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const r = rows[cursor];
      if (r && !chosenIds.has(r.id)) add(r);
    }
  };

  const filterKeys = ['department', 'title', 'userType'].filter((f) => facets[f]?.length);
  const hasFilter = !!(filters.department || filters.title || filters.userType);

  return (
    <div ref={rootRef}>
      {/* Chosen approvers — each with its own contact mode. */}
      {chosen.length > 0 && (
        <ul className="flex flex-col gap-1.5 mb-2">
          {chosen.map((v) => (
            <li key={v.id} className="rounded-v2-md border border-v2-border px-2.5 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-[13px] text-v2-ink truncate block">
                    {v.name || v.email || v.id}
                    {v.legacy ? (
                      <Pill tone="danger" size="sm" className="ml-1.5">Invalid</Pill>
                    ) : v.unresolved ? (
                      <Pill tone="warning" size="sm" className="ml-1.5">Hidden</Pill>
                    ) : null}
                  </span>
                  {/* One short line, in the muted tone the rest of the row uses. An earlier version
                      explained WHY the entry was invalid — which release it came from, what the old
                      form did, where the escalation went instead. None of that is the operator's
                      problem; they need to know it is wrong and what to do, in the width of a row.
                      The history belongs in this comment, not on their screen. */}
                  {v.legacy && (
                    <span className="block text-[11.5px] text-v2-ink-muted">
                      Not a person. Remove it and choose someone.
                    </span>
                  )}
                  {!v.legacy && v.unresolved && (
                    <span className="block text-[11.5px] text-v2-ink-muted">
                      Outside your access. The policy still works.
                    </span>
                  )}
                  {v.email && v.name !== v.email && (
                    <span className="block text-[11.5px] text-v2-ink-muted truncate">{v.email}</span>
                  )}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => remove(v.id)}
                  aria-label={`Remove ${v.name || v.email}`}
                  className="shrink-0 text-v2-ink-muted hover:text-v2-danger disabled:opacity-40"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="flex items-center gap-1.5 mt-1.5">
                <ModeButton
                  active={v.mode !== 'MANAGER_OF'}
                  disabled={disabled}
                  onClick={() => setMode(v.id, 'USER')}
                  label="Contact them"
                />
                <ModeButton
                  active={v.mode === 'MANAGER_OF'}
                  disabled={disabled || !v.hasManager}
                  onClick={() => setMode(v.id, 'MANAGER_OF')}
                  label={v.hasManager && v.managerName
                    ? `Contact their manager (${v.managerName})`
                    : 'Contact their manager'}
                  hint={!v.hasManager
                    ? 'No manager on file for this person — run a directory sync to populate it.'
                    : 'Resolved when the escalation fires, so a change of manager re-routes automatically.'}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <div ref={anchorRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { setPage(0); setOpen((o) => !o); }}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-v2-md border
          border-v2-border bg-v2-surface text-left text-[13px] text-v2-ink
          hover:border-v2-border-strong focus:outline-none focus:border-v2-action
          ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className="flex items-center gap-2">
          <User size={14} className="text-v2-ink-muted" />
          {chosen.length === 0 ? 'Choose who to contact' : 'Add another person'}
        </span>
        <ChevronUp size={14} className={`text-v2-ink-muted transition ${open ? '' : 'rotate-180'}`} />
      </button>

      {open && (
        <div
          onKeyDown={onKeyDown}
          style={{ maxHeight: panel.maxHeight }}
          className={`absolute z-30 w-full flex flex-col rounded-v2-md border border-v2-border
            bg-v2-surface shadow-v2-md overflow-hidden
            ${panel.dir === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'}`}
        >
          <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-v2-border">
            <Search size={14} className="text-v2-ink-muted shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder="Search by name or email"
              className="w-full bg-transparent text-[13px] text-v2-ink placeholder:text-v2-ink-muted focus:outline-none"
            />
          </div>

          {/* Facets appear only where the directory actually populates them — an empty dropdown
              reads as broken rather than as "your IGA does not send this attribute". */}
          {filterKeys.length > 0 && (
            <div className="shrink-0 flex flex-wrap gap-2 px-3 py-2 border-b border-v2-border">
              {filterKeys.map((f) => (
                <select
                  key={f}
                  value={filters[f]}
                  onChange={(e) => onFilter(f, e.target.value)}
                  className="text-[12px] border border-v2-border rounded-v2-sm px-1.5 py-1 bg-v2-surface"
                >
                  <option value="">{f === 'userType' ? 'Any type' : `Any ${f}`}</option>
                  {facets[f].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              ))}
            </div>
          )}

          {/* The only scrolling region in the panel, so the search box above and the paging footer
              below stay put and stay reachable however long the page of results is. */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {error ? (
              <div className="px-3 py-4 text-[12.5px] text-v2-danger flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />{error}
              </div>
            ) : loading ? (
              <div className="px-3 py-4 text-[12.5px] text-v2-ink-muted">Searching…</div>
            ) : rows.length === 0 ? (
              <div className="px-3 py-4 text-[12.5px] text-v2-ink-muted">
                {debounced || hasFilter
                  ? 'Nobody matches that. Try fewer words, or clear the filters.'
                  : 'No one is available to choose. Users appear here once they exist on the platform '
                    + 'or arrive from a directory sync.'}
              </div>
            ) : (
              <ul role="listbox" aria-label="Directory results" className="py-1">
                {rows.map((r, i) => {
                  const already = chosenIds.has(r.id);
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={already}
                        disabled={already}
                        onMouseEnter={() => setCursor(i)}
                        onClick={() => add(r)}
                        className={`w-full text-left px-3 py-2 text-[13px] hover:bg-v2-bg-alt
                          ${i === cursor && !already ? 'bg-v2-bg-alt' : ''}
                          ${already ? 'opacity-45 cursor-default' : ''}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="truncate">{r.name || r.email}</span>
                          {already && <Pill tone="muted" size="sm">Added</Pill>}
                          {!r.directorySynced && <Pill tone="muted" size="sm">Local</Pill>}
                        </span>
                        <span className="block text-[11.5px] text-v2-ink-muted truncate">
                          {[r.email, r.title, r.department].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Why every name here says "Local" and every manager toggle is dead.
              Without this the operator sees a handful of platform accounts and a greyed-out
              "contact their manager" with no way to tell "no directory is connected" from "the
              sync is broken" — and those need completely different actions. The reason lives on
              screen rather than in a title attribute nobody hovers. */}
          {!error && !loading && rows.length > 0 && rows.every((r) => !r.directorySynced) && (
            <div className="shrink-0 px-3 py-2 border-t border-v2-border text-[11.5px] text-v2-ink-muted">
              Everyone here is a local platform account. No identity directory has synced into this
              organization, so nobody has a manager on file and “contact their manager” stays
              unavailable. Connect an IGA and use “Sync from IGA” to change that.
            </div>
          )}

          {!error && (
            <PickerPagingFooter
              page={page}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onPage={setPage}
              noun="people"
            />
          )}
        </div>
      )}
      </div>
    </div>
  );
}

function ModeButton({ active, disabled, onClick, label, hint }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={hint}
      className={`text-[11.5px] px-2 py-1 rounded-v2-sm border transition
        ${active ? 'border-v2-action text-v2-ink' : 'border-v2-border text-v2-ink-muted'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-v2-border-strong'}`}
    >
      {label}
    </button>
  );
}

export default ApproverPicker;
